import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';
import intersection from 'lodash.intersection';
import MediaGalleryDetailsDialog from './MediaGalleryDetailsDialog';
import {PictureSortDirection, PictureSortType} from './MediaGalleryOptionsPopout';
import MediaGalleryAlbumThumbnail from './MediaGalleryAlbumThumbnail';
import MediaGalleryThumbnailFactory, {
  ThumbnailLayout,
} from './Thumbnails/MediaGalleryThumbnailFactory';
import {Checkbox, Heading} from '@taopix/taopix-design-system';
import {hasExpired} from '../../Utils/Asset';
import {HeadingLevels} from '@taopix/taopix-design-system/dist/types/Components/Heading/Heading';
import {ThumbnailOverlayDisplayMode} from './Thumbnails/ThumbnailOverlays';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {sortImages} from '../../Utils/MediaGallery';
import {ThumbnailSelectorProps} from './Thumbnails/ThumbnailSelector';
import {PictureOrientation} from '@taopix/taopix-core';

export enum HideMode {
  Usage,
  Project,
}

export enum SelectionMode {
  Single = 'single',
  Multi = 'multi',
}

export type ExternalAsset = Omit<AssetMetaInterface, 'fileBlobURL'> & {
  onSelectPhoto: Function;
  externalid: number;
  libraryLinkType: string;
  libraryLinkPath: string;
};

export type Asset = AssetMetaInterface | ExternalAsset;

export type PictureUsage = {
  [key: number]: number;
};

export type MediaGalleryProps<T extends Asset = Asset> = {
  sortBy: PictureSortType;
  sortDirection: PictureSortDirection;
  imageList: Array<T>;
  pictureUsage?: PictureUsage;
  hideUsed?: boolean;
  selectableImages?: boolean;
  groupedView?: boolean;
  thumbnailLayout?: ThumbnailLayout;
  detailsPopoutAnchorId?: string;
  thumbnailSize?: number; // For proportional grids, the size is used this as a basis, but thumbnail sizes are calculated dynamically to create a neat grid
  direction?: 'column' | 'row';
  showHeadings?: boolean;
  headingSize?: HeadingLevels;
  selectedImages?: Array<number | string>;
  onSelectAlbum?: Function;
  localeString?: string;
  currencyCode?: string;
  showPricing?: boolean;
  showUsage?: boolean;
  showLabel?: boolean;
  showTooltips?: boolean;
  tooltipDirection?: 'top' | 'bottom' | 'right' | 'left';
  isExternalService?: boolean;
  selectionAppearance?: ThumbnailSelectorProps['appearance'];
  isSmallScreen?: boolean;
  disabledAssetIds?: Array<string | number>;
  style?: object;
  columns?: number;
  showPreviewButton?: boolean;
  showDeleteButton?: boolean;
  gridGap?: number;
  overlayDisplayMode?: ThumbnailOverlayDisplayMode;
  maxSelection?: number;
  selectionMode?: SelectionMode;
  hideMode?: HideMode;
  onSelectPicture?: (checked: boolean, pic: any) => void;
  onItemDragStart?: (event: React.DragEvent<HTMLElement>, asset: T) => void;
  onItemDragEnd?: (event: React.DragEvent<HTMLElement>, asset: T) => void;
  onItemTouchStart?: (event: React.TouchEvent<HTMLElement>) => void;
  onItemTouchEnd?: (event: React.TouchEvent<HTMLElement>) => void;
  onRotateAsset?: (id: number) => void;
  onDeleteAsset?: (id: number) => void;
  onSelectGroup?: (checked: boolean, payload: T[]) => void;
  onKeyDown?: (event: React.KeyboardEvent, asset: Asset) => void;
};

const MediaGallery = ({
  sortBy,
  sortDirection,
  imageList,
  thumbnailLayout,
  pictureUsage = {},
  hideUsed = false,
  selectableImages = false,
  groupedView = false,
  detailsPopoutAnchorId,
  thumbnailSize = 120,
  direction = 'column',
  selectedImages = [],
  onSelectAlbum = () => {},
  localeString = 'en',
  currencyCode = 'GBP',
  showHeadings = true,
  headingSize = 2,
  showPricing = false,
  showUsage = false,
  showTooltips = true,
  tooltipDirection = 'right',
  isExternalService = false,
  selectionAppearance = 'checkbox',
  isSmallScreen,
  disabledAssetIds,
  columns,
  showPreviewButton = true,
  showDeleteButton = false,
  showLabel = false,
  gridGap = 5,
  overlayDisplayMode = 'hover',
  maxSelection,
  hideMode = HideMode.Usage,
  onSelectPicture,
  onItemDragStart,
  onItemDragEnd,
  onItemTouchStart,
  onItemTouchEnd,
  onRotateAsset,
  onDeleteAsset,
  onSelectGroup,
  onKeyDown,
  ...props
}: MediaGalleryProps) => {
  const {t, i18n} = useTranslation(['picturemanager']);
  const id = useId().replace(new RegExp(':', 'g'), '');

  const [detailsDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<Asset>();
  const [instanceId] = useState<string>(`tpx-media-gallery_${id}`);
  const [groups, setGroups] = useState<string[]>([]);
  const [disabledGroups, setDisabledGroups] = useState<string[]>([]);

  const getPictureOrientation = (value: string): string => {
    switch (value) {
      case 'square': {
        return t('str_LabelTitleSquare');
      }
      case 'landscape': {
        return t('str_LabelTitleLandscape');
      }
      case 'portrait': {
        return t('str_LabelTitlePortrait');
      }
      default: {
        return '';
      }
    }
  };

  //TODO: Needs to retrieve format from the format.conf?
  const groupBy = useCallback(
    (array: Asset[], key: string, direction: PictureSortDirection): Record<string, Asset[]> => {
      // Return the reduced array
      const grouped = array.reduce((result, currentItem) => {
        let resultKey = '';
        if (key === 'datetimeoriginal') {
          // default to the fileDateModified
          let date = new Date(currentItem.fileDateModified);
          // if we actually have the correct meta data
          if (!isNaN(Date.parse(currentItem?.metaData?.dateTimeOriginal))) {
            date = new Date(currentItem?.metaData?.dateTimeOriginal);
          }

          const derivedDate = new Date();
          derivedDate.setMonth(date.getMonth());
          derivedDate.setFullYear(date.getFullYear());
          // mock the day to the 1st of the month to help with grouping
          derivedDate.setDate(1);

          // cast the date to US date (all browsers will handle this correctly)
          resultKey = derivedDate.toLocaleString('en-US', {
            year: 'numeric',
            day: 'numeric',
            month: 'numeric',
          });
        } else {
          resultKey = currentItem[key];
        }
        // If an array already present for key, push it to the array. Otherwise create an array and push the object.
        (result[resultKey] = result[resultKey] || []).push(currentItem);
        // return the current iteration `result` value, this will be the next iteration's `result` value and accumulate
        return result;
      }, {}) as Record<string, Asset[]>; // Empty array is the initial value for result object

      // return here if the sort key is not datetimeoriginal
      if (!['datetimeoriginal', 'orientation'].includes(key)) {
        return grouped;
      }

      if ('orientation' === key) {
        return Object.keys(grouped)
          .sort((a, b) => {
            let sortedA = a;
            let sortedB = b;
            if ('desc' === direction) {
              sortedA = b;
              sortedB = a;
            }
            if (Number(sortedA) === Number(sortedB)) return 0;
            return Number(sortedA) < Number(sortedB) ? -1 : 1;
          })
          .reduce((acc, curr) => {
            const formatted = (() => {
              switch (Number(curr) as unknown as PictureOrientation) {
                case PictureOrientation.Square: {
                  return 'square';
                }
                case PictureOrientation.Landscape: {
                  return 'landscape';
                }
                case PictureOrientation.Portrait: {
                  return 'portrait';
                }
              }
            })();

            if (!acc[formatted]) {
              acc[formatted] = grouped[curr];
            }
            return acc;
          }, {});
      }

      // otherwise sort by month/year
      return Object.keys(grouped)
        .sort((a, b) => {
          let sortedA = b;
          let sortedB = a;
          if ('desc' === direction) {
            sortedA = a;
            sortedB = b;
          }
          return (Date.parse(sortedB) as any) - (Date.parse(sortedA) as any);
        })
        .reduce((acc, curr) => {
          const langCode = i18n.language.startsWith('zh')
            ? i18n.language.split('_').join('-')
            : i18n.language;
          // format the key to the i18n language
          const formattedKey = new Date(curr).toLocaleString(langCode, {
            year: 'numeric',
            month: 'long',
          });

          if (!acc[formattedKey]) {
            acc[formattedKey] = grouped[curr];
          }
          return acc;
        }, {});
    },
    []
  );

  /**
   * Sorts the image list by the given sort type
   * Filters out images that have been used if hideUsed is true
   * Groups the images by the given group type
   */
  const assets = useMemo(() => {
    let images = [...imageList];

    if (HideMode.Usage === hideMode) {
      images = images.filter(image => {
        return !(hideUsed && pictureUsage[image.id] && pictureUsage[image.id] > 0);
      });
    }

    // this is for previous uploads
    if (HideMode.Project === hideMode) {
      images = images.filter(image => {
        return !(hideUsed && disabledAssetIds?.includes(image.id));
      });
    }

    if (sortBy || sortDirection) {
      images = sortImages(sortBy, sortDirection, images);
    }

    // if grouped view is on, ensure only datetimeoriginal & orientation are valid groups
    if (groupedView && ['datetimeoriginal', 'orientation'].includes(sortBy)) {
      return Object.entries(groupBy(images, sortBy, sortDirection));
    }

    return [['', images]];
  }, [imageList, hideUsed, sortBy, sortDirection, groupedView, pictureUsage, groupBy]);

  useEffect(() => {
    if (!groupedView) {
      return;
    }

    if (!['datetimeoriginal', 'orientation'].includes(sortBy)) {
      return;
    }

    assets.forEach((group: any) => {
      const groupName = group[0].split(' ').join('_').trim();
      if ('' === groupName || groups.includes(groupName)) {
        return;
      }

      // all the assets in the group are disabled, disable the group
      if (
        intersection(
          disabledAssetIds,
          group[1].map(a => a.id)
        ).length === group[1].length
      ) {
        setGroups(current => {
          return Array.from(new Set([...current, groupName]));
        });

        setDisabledGroups(current => {
          return Array.from(new Set([...current, groupName]));
        });
      }
    });
  }, [assets, groups, disabledAssetIds, groupedView, sortBy]);

  const maxSelectionReached = (() => {
    if (typeof maxSelection === 'undefined') {
      return false;
    }

    if (1 === maxSelection) {
      return false;
    }

    return selectedImages.length === maxSelection;
  })();

  // If a selection mode is passed, use that, otherwise calculate based on the number of allowed selections.
  const selectionMode =
    typeof props.selectionMode !== 'undefined'
      ? props.selectionMode
      : typeof maxSelection !== 'undefined' && maxSelection <= 1
      ? SelectionMode.Single
      : SelectionMode.Multi;

  const onClickImage = (checked: boolean, image: Asset) => {
    if (onSelectPicture) {
      onSelectPicture(checked, image);
    }

    if (checked) {
      setSelectedImage(image);
    } else {
      setSelectedImage(undefined);
    }
  };

  const detailsDialogTriggerButtonRef = useRef<HTMLButtonElement | null>(null);

  const openDetailsDialog = (
    image: Asset,
    triggerButtonRef: React.RefObject<HTMLButtonElement>
  ) => {
    setDialogOpen(true);
    setSelectedImage(image);
    // Set the aria-expanded attribute to true on the button that opened the dialog
    detailsDialogTriggerButtonRef.current = triggerButtonRef.current;
    detailsDialogTriggerButtonRef.current?.setAttribute('aria-expanded', 'true');
  };

  const closeDetailsDialog = () => {
    setDialogOpen(false);
    // Set the aria-expanded attribute to false on the button that opened the dialog
    detailsDialogTriggerButtonRef.current?.setAttribute('aria-expanded', 'false');
    detailsDialogTriggerButtonRef.current?.focus();
  };

  const deleteAsset = (id: number) => {
    if (onDeleteAsset) {
      setDialogOpen(false);
      setSelectedImage(undefined);
      onDeleteAsset(id);
    }
  };

  const rotateAsset = (id: number) => {
    if (onRotateAsset) {
      onRotateAsset(id);
      let rotation = (selectedImage?.rotation || 0) - 90;

      if (rotation < 0) {
        rotation += 360;
      }

      setSelectedImage({
        ...selectedImage,
        rotation,
      } as Asset);
    }
  };

  const selectGroup = useCallback(
    (checked: boolean, name: string, payload: any) => {
      if (!checked) {
        setGroups(groups => {
          return groups.filter(g => g !== name);
        });
      } else {
        setGroups(groups => {
          return [...groups].concat(name);
        });
      }
      onSelectGroup &&
        onSelectGroup(
          checked,
          payload.filter(a => !disabledAssetIds?.includes(a.id)) // remove any already disabled assets
        );
    },
    [disabledAssetIds]
  );

  const formatGroupName = (str: any): string => {
    if (PictureSortType.Orientation === sortBy) {
      return getPictureOrientation(str);
    }
    return str;
  };

  let selectedImageUsage = 0;
  if (selectedImage) {
    selectedImageUsage = pictureUsage[selectedImage.id] ? pictureUsage[selectedImage.id] : 0;
  }

  // Set up the variables required by the thumbnails CSS
  const cssConfig = {
    '--grid-gap': gridGap + 'px',
    '--target-thumbnail-height': thumbnailSize,
    '--columns': columns,
  } as React.CSSProperties;

  const sectionClassNames = cx({
    'mr-xl': direction === 'row',
    'mb-xl': direction !== 'row',
  });

  const containerClassNames = cx(['pb-px', 'flex', direction === 'row' ? 'flex-row' : 'flex-col']);

  const galleryClassNames = cx([
    'flex',
    direction === 'column' ? 'flex-wrap' : 'flex-row',
    thumbnailLayout === 'full-frame-tiles' && ['-mt-[var(--grid-gap)]', '-ml-[var(--grid-gap)]'],
    (thumbnailLayout === 'square-tiles' || thumbnailLayout === 'details-list') && [
      'grid',
      'gap-[var(--grid-gap)]',
      columns
        ? 'grid-cols-[repeat(var(--columns),1fr)]'
        : 'grid-cols-[repeat(auto-fill,minmax(calc(var(--target-thumbnail-height)*1rem),1fr))]',
    ],
  ]);

  return (
    <div id={instanceId} className={containerClassNames} style={cssConfig}>
      {assets.map((imageListGroup: any) => {
        const groupName = imageListGroup[0].split(' ').join('_').trim();
        return (
          <div key={imageListGroup[0]}>
            <section className={sectionClassNames}>
              {imageListGroup[0] && showHeadings && (
                <div className="flex justify-between sticky z-10 top-0 py-sm bg-themeBackgroundColour text-themeTextColour">
                  <Heading level={1} size={headingSize}>
                    {formatGroupName(imageListGroup[0])}
                  </Heading>
                  {onSelectGroup && (
                    <Checkbox
                      label={t('*:str_LabelSelectAllPictures')}
                      id={`${instanceId}_${groupName}`}
                      onChange={(isChecked: boolean) => {
                        selectGroup(isChecked, groupName, imageListGroup[1]);
                      }}
                      defaultChecked={groups.includes(groupName)}
                      disabled={disabledGroups.includes(groupName)}
                      className="mr-24"
                    />
                  )}
                </div>
              )}

              <ul className={galleryClassNames}>
                {imageListGroup[1].map((image: Asset, index: number) => {
                  const usage = pictureUsage[image.id] ? pictureUsage[image.id] : 0;
                  // if we are an asset from an external source, use the external id to test if the asset is selected
                  const selected =
                    isExternalService && image.hasOwnProperty('externalid')
                      ? selectedImages.includes((image as ExternalAsset).externalid)
                      : selectedImages.includes(image.id);

                  const expired = image?.link?.expirationDate
                    ? hasExpired(image?.link?.expirationDate)
                    : false;
                  const disabled = maxSelectionReached
                    ? !selectedImages.includes(image.id)
                    : !!disabledAssetIds?.includes(image.id);
                  const key = `${image.ref}`;

                  if ((image as ExternalAsset).libraryLinkType === 'folder') {
                    return (
                      <MediaGalleryAlbumThumbnail
                        albumId={image.id}
                        imgSource={image.src}
                        albumName={image.name}
                        onSelect={() => {
                          onSelectAlbum(image.id, (image as ExternalAsset).libraryLinkPath);
                        }}
                        key={key}
                        columns={columns}
                        usePictureCount={false}
                      />
                    );
                  } else {
                    return (
                      <MediaGalleryThumbnailFactory
                        key={key}
                        thumbnailLayout={thumbnailLayout}
                        disabled={disabled}
                        expired={expired}
                        localeString={localeString}
                        currencyCode={currencyCode}
                        selected={selected}
                        onDragStart={onItemDragStart || undefined}
                        onDragEnd={onItemDragEnd || undefined}
                        onTouchStart={onItemTouchStart || undefined}
                        onTouchEnd={onItemTouchEnd || undefined}
                        selectableImages={selectableImages}
                        onSelectPicture={onClickImage}
                        used={usage}
                        image={image}
                        openDetailsDialog={showPreviewButton ? openDetailsDialog : undefined}
                        showDeleteButton={showDeleteButton}
                        showPricing={showPricing}
                        showUsage={showUsage}
                        showTooltips={showTooltips}
                        showLabel={showLabel}
                        selectionAppearance={selectionAppearance}
                        columns={columns}
                        onDeleteAsset={deleteAsset}
                        overlayDisplayMode={overlayDisplayMode}
                        maxSelectionReached={maxSelectionReached}
                        selectionMode={selectionMode}
                        onKeyDown={onKeyDown}
                      />
                    );
                  }
                })}
              </ul>
            </section>
          </div>
        );
      })}
      <MediaGalleryDetailsDialog
        anchorId={detailsPopoutAnchorId}
        onClose={closeDetailsDialog}
        excludeIdFromClickOutside={instanceId}
        isOpen={detailsDialogOpen}
        image={selectedImage}
        closeEvent={closeDetailsDialog}
        showUsage={showUsage}
        used={selectedImageUsage}
        locale={localeString}
        onRotateAsset={onRotateAsset ? rotateAsset : undefined}
        onDeleteAsset={onDeleteAsset ? deleteAsset : undefined}
      />
    </div>
  );
};

export default MediaGallery;
